import { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { useSearchParams } from "react-router";
import PageMeta from "../components/common/PageMeta";
import PageContainer from "../components/common/PageContainer";
import PageHeader from "../components/common/PageHeader";
import DataFilter from "../components/common/DataFilter";
import ReportTable from "../components/reports/ReportTable";
import MetricCard from "../components/common/MetricCard";
import StatGrid from "../components/common/StatGrid";
import { formatReportCell } from "../utils/reportCells";
import { presets, resolveRangeFromParams, type PresetKey } from "../components/common/dataFilterPresets";
import { useApi } from "../hooks/useApi";
import { useCompanyId } from "../hooks/useCompanyId";
import { useBrandId } from "../hooks/useBrandId";
import { useStateValue } from "../context/StateValueContext";
import { REPORT_CARD_BRANDS, type ReportCardBrand, type ReportsOverviewResponse } from "../types/reports";

type ReportTableKey = Exclude<keyof ReportsOverviewResponse, "range" | "lastWrittenDate" | "companyName" | "brandName" | "paypalCompanyLevelOnly" | "processorsByCardBrand">;

const TABLES: { key: ReportTableKey; title: string }[] = [
  { key: "processors", title: "Risk metrics by processor" },
  { key: "paypal", title: "PayPal risk metrics" },
  { key: "chargebacksByProcessorMonth", title: "Chargebacks by processor" },
  { key: "chargebacksByReason", title: "Chargebacks by reason category" },
  { key: "chargebacksByCardBrand", title: "Chargebacks by card brand" },
  { key: "alertsByMonth", title: "Alerts" },
];

const CARDS: { label: string; table: "processors" | "alertsByMonth"; cell: string; note: string }[] = [
  { label: "Risk Rate", table: "processors", cell: "riskRate", note: "Chargebacks and alerts as a share of transactions." },
  { label: "Chargeback Rate", table: "processors", cell: "hardChargebackRate", note: "Chargebacks as a share of transactions." },
  { label: "Prevention Rate", table: "alertsByMonth", cell: "preventionRate", note: "Share of disputes caught as alerts before they became chargebacks." },
];

export default function Reports() {
  const { get, loading } = useApi();
  const [{ user }] = useStateValue();
  const { companyId: pickedCompanyId } = useCompanyId();
  const companyId = pickedCompanyId ?? user?.companyId ?? null;
  const { brandId } = useBrandId();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialRange = resolveRangeFromParams(searchParams, presets, "thisMonth");
  const [preset, setPreset] = useState<PresetKey>(initialRange.preset);
  const [startDate, setStartDate] = useState<Dayjs | null>(initialRange.start);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialRange.end);
  const [data, setData] = useState<ReportsOverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cardBrand, setCardBrand] = useState<ReportCardBrand | null>(null);

  const handlePresetChange = (next: PresetKey) => {
    setPreset(next);
    if (next !== "custom") {
      setStartDate(presets[next].start());
      setEndDate(presets[next].end());
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("range", preset);
    if (startDate) params.set("start", startDate.format("YYYY-MM-DD"));
    else params.delete("start");
    if (endDate) params.set("end", endDate.format("YYYY-MM-DD"));
    else params.delete("end");
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, startDate, endDate]);

  useEffect(() => {
    setData(null);
    setError(null);
    if (!companyId || !startDate || !endDate) return;
    const params = new URLSearchParams({
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
      companyId: companyId.toString(),
    });
    if (brandId) params.set("brandId", String(brandId));
    // A response for a company or range the page has since left must not land.
    let current = true;
    get<ReportsOverviewResponse>(`/api/reports/overview?${params.toString()}`)
      .then((response) => { if (current) setData(response); })
      .catch((err) => { if (current) setError(err instanceof Error ? err.message : "Failed to fetch the report"); });
    return () => { current = false; };
  }, [brandId, companyId, get, startDate, endDate]);

  return (
    <>
      <PageMeta title="Chargemont | Reports" description="Chargemont | Reports" />
      <PageContainer>
        <PageHeader title="Reports" />
        <Box display="flex" flexDirection="column" gap={3}>
          <DataFilter
            preset={preset}
            startDate={startDate}
            endDate={endDate}
            onPresetChange={handlePresetChange}
            onStartDateChange={(d) => { setStartDate(d); setPreset("custom"); }}
            onEndDateChange={(d) => { setEndDate(d); setPreset("custom"); }}
            minDate={dayjs.utc().subtract(1, "year")}
          />

          {!companyId && (
            <Alert severity="info">Select a company from the header to view reports.</Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {companyId && loading && (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          )}

          {data && (
            <Typography variant="body2" color="text.secondary">
              Company: <strong>{data.companyName ?? "Unknown"}</strong> · Brand: <strong>{data.brandName ?? "All brands"}</strong>
            </Typography>
          )}

          {data && startDate && startDate.format("YYYY-MM-DD") > data.lastWrittenDate && (
            <Alert severity="info">Data for this period is still being gathered.</Alert>
          )}

          {data && (
            <StatGrid cols={3}>
              {CARDS.map(({ label, table, cell, note }) => (
                <MetricCard
                  key={cell}
                  label={label}
                  value={formatReportCell("rate", data[table].rows.find((r) => r.isTotal)?.cells[cell])}
                  note={note}
                />
              ))}
            </StatGrid>
          )}

          {data && TABLES.map(({ key, title }) => {
            if (key === "paypal" && data.paypalCompanyLevelOnly) {
              return (
                <Box key={key} component="section" aria-label={title}>
                  <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
                  <Alert severity="info">PayPal risk metrics are only available at company level. Select all brands to see them.</Alert>
                </Box>
              );
            }
            const brand = key === "processors" ? cardBrand : null;
            const table = brand ? data.processorsByCardBrand[brand] : data[key];
            return table && (
              <ReportTable
                key={key}
                title={title}
                table={table}
                csvFilename={`${title.toLowerCase().replace(/\W+/g, "-")}${brand ? `_${brand.toLowerCase()}` : ""}_${data.range.startDate}_${data.range.endDate}.csv`}
                toolbar={key === "processors" && (
                  <ToggleButtonGroup
                    value={cardBrand ?? "all"}
                    exclusive
                    size="small"
                    aria-label="Card brand"
                    onChange={(_, next: ReportCardBrand | "all" | null) => { if (next !== null) setCardBrand(next === "all" ? null : next); }}
                    sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.5 } }}
                  >
                    <ToggleButton value="all">All card brands</ToggleButton>
                    {REPORT_CARD_BRANDS.map((b) => <ToggleButton key={b} value={b}>{b}</ToggleButton>)}
                  </ToggleButtonGroup>
                )}
              />
            );
          })}
        </Box>
      </PageContainer>
    </>
  );
}
